import { Box } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { JSX } from "react";

import type { SalesEmailConfidence, SalesOrgLeadDetail } from "@xop/dashboard-shared";

import type { HeatPillDisplay, SxStyle } from "../heatStyles.js";
import { rfpDeadlineMeta, type RfpDeadlineMeta } from "../quality.js";

/**
 * Small presentational badges + notes for the lead detail page, extracted from
 * `LeadPage.tsx`. All are pure functions of their props (no hooks), so they
 * render-test trivially.
 */

export function HeatBadge({ display }: { display: HeatPillDisplay }): JSX.Element {
  return (
    <Box
      component="span"
      {...(display.title ? { title: display.title } : {})}
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 999,
          px: 1,
          py: 0.25,
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          whiteSpace: "nowrap",
        },
        display.sx,
      ]}
    >
      {display.label}
    </Box>
  );
}

/** The RFP / tender deadline pill — the explicit answer to "did the AI count
 * the due date?". Shows the date + how far away it is; an EXPIRED window reads
 * as a warning ("RFP closed Apr 6, 2026 · 61 days ago") and is WHY the agent
 * cooled the lead's heat. Renders nothing when the lead has no deadline. */
export function RfpDeadlineBadge({ lead }: { lead: SalesOrgLeadDetail }): JSX.Element | null {
  const meta: RfpDeadlineMeta | null = rfpDeadlineMeta(lead);
  if (!meta) return null;
  // "100% proof" gate (2026-06-16): never assert a dated "RFP due <date>" pill
  // for a deadline that wasn't verified against its source — the DeadlineProofNote
  // below explains the suppression instead, so the two surfaces can't contradict.
  if (lead.deadline_verified !== true) return null;
  const color = (t: Theme): string =>
    meta.tone === "good"
      ? t.palette.success.main
      : meta.tone === "warn"
        ? t.palette.warning.main
        : t.palette.text.disabled;
  return (
    <Box
      component="span"
      title={
        meta.expired
          ? "The RFP response window has closed — the agent cooled this lead's heat, but kept it for its contact + fit value."
          : "The RFP response deadline the agent captured from the source."
      }
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.375,
        borderRadius: 999,
        px: 1,
        py: 0.25,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color,
        bgcolor: (t: Theme) => alpha(color(t), 0.12),
        border: 1,
        borderColor: (t: Theme) => alpha(color(t), 0.4),
      }}
    >
      <Box component="span" aria-hidden>⏱</Box> {meta.text}
    </Box>
  );
}

/** The deadline "100% proof" note (2026-06-16). When a lead carries a deadline,
 * show WHETHER it was proven against the cited source — and the verbatim proof
 * sentence when it was. A date that wasn't proven shows no countdown (the
 * {@link leadUrgency} gate); this line tells the operator WHY, so a suppressed
 * "super hot" never looks like a bug. Renders nothing when there's no deadline
 * (nothing to verify). */
export function DeadlineProofNote({ lead }: { lead: SalesOrgLeadDetail }): JSX.Element | null {
  const deadline = lead.deadline ?? lead.rfp_deadline ?? null;
  if (!deadline) return null;
  const verified = lead.deadline_verified === true;
  const checked = lead.deadline_verified === false; // checked but not proven
  const proof = (lead.deadline_proof ?? "").trim();
  const tone: "good" | "warn" | "muted" = verified
    ? "good"
    : checked
      ? "warn"
      : "muted";
  const color = (t: Theme): string =>
    tone === "good"
      ? t.palette.success.main
      : tone === "warn"
        ? t.palette.warning.main
        : t.palette.text.disabled;
  const headline = verified
    ? "Deadline verified against the cited source"
    : checked
      ? "Deadline NOT verified — countdown suppressed"
      : "Deadline not yet verified — countdown suppressed until checked";
  return (
    <Box
      sx={{
        mt: 0.875,
        display: "flex",
        alignItems: "flex-start",
        gap: 0.625,
        maxWidth: 640,
        fontSize: 12,
        lineHeight: 1.4,
        color: (t: Theme) => color(t),
      }}
    >
      <Box component="span" aria-hidden sx={{ fontWeight: 700 }}>
        {verified ? "✓" : "⚠"}
      </Box>
      <Box component="span" sx={{ color: "text.secondary" }}>
        <Box component="span" sx={{ fontWeight: 700, color: (t: Theme) => color(t) }}>
          {headline}.
        </Box>{" "}
        {proof ? (
          verified ? (
            <>“{proof}”</>
          ) : (
            <>{proof}.</>
          )
        ) : null}
      </Box>
    </Box>
  );
}

/** Marks a SOLUTION-LED "fit" lead — found by reasoning from an XOP capability
 * to a non-obvious vertical (e.g. car-parts → Hoot-n-Holler), as opposed to a
 * dated trigger. Only shown for fit leads (trigger is the default + the norm),
 * so it reads as "this one's a smart capability match, capped at WARM". */
export function FitBadge(): JSX.Element {
  return (
    <Box
      component="span"
      title="Solution-led fit — matched from one of our capabilities to this org's operation (no dated trigger; capped at warm)."
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.375,
        borderRadius: 999,
        px: 1,
        py: 0.25,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: (t: Theme) => t.palette.info.main,
        bgcolor: (t: Theme) => alpha(t.palette.info.main, 0.12),
        border: 1,
        borderColor: (t: Theme) => alpha(t.palette.info.main, 0.4),
      }}
    >
      <Box component="span" aria-hidden>🎯</Box> Solution fit
    </Box>
  );
}

/** The strongest possible signal — this lead's org is now a CRM customer. */
export function ConvertedBadge(): JSX.Element {
  return (
    <Box
      component="span"
      title="This lead's organisation is now a customer in the CRM — it converted."
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.375,
        borderRadius: 999, px: 1, py: 0.25, fontSize: 11, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.05em",
        color: (t: Theme) => t.palette.success.main,
        bgcolor: (t: Theme) => alpha(t.palette.success.main, 0.14),
        border: 1, borderColor: (t: Theme) => alpha(t.palette.success.main, 0.45),
      }}
    >
      <Box component="span" aria-hidden>✓</Box> Converted to customer
    </Box>
  );
}

/** The operator qualified this lead into the CRM as a prospect (a CRM Lead
 * tagged "AI created prospect") — awaiting conversion to a customer. */
export function InCrmBadge(): JSX.Element {
  return (
    <Box
      component="span"
      title="Qualified into the CRM as a prospect (tagged 'AI created prospect') — awaiting conversion to a customer."
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.375,
        borderRadius: 999, px: 1, py: 0.25, fontSize: 11, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.05em",
        color: "text.secondary",
        bgcolor: (t: Theme) => alpha(t.palette.text.primary, 0.06),
        border: 1, borderColor: "divider",
      }}
    >
      <Box component="span" aria-hidden>★</Box> Qualified
    </Box>
  );
}

const EMAIL_CONF_STYLES: Record<SalesEmailConfidence, { label: string; dot: SxStyle; title: string }> = {
  verified: { label: "verified", dot: { bgcolor: "success.main" },  title: "SMTP RCPT TO accepted — deliverable" },
  likely:   { label: "likely",   dot: { bgcolor: "warning.main" },  title: "Domain has MX records but RCPT was inconclusive" },
  guessed:  { label: "guessed",  dot: { bgcolor: "text.disabled" }, title: "Pattern guess — no validation" },
  unknown:  { label: "unknown",  dot: { bgcolor: "divider" },       title: "Confidence unknown" },
};

export function EmailConfidenceDot({
  confidence,
}: { confidence: SalesEmailConfidence }): JSX.Element {
  const style = EMAIL_CONF_STYLES[confidence] ?? EMAIL_CONF_STYLES.unknown;
  return (
    <Box
      component="span"
      title={`${style.label} — ${style.title}`}
      sx={[{ display: "inline-block", height: 8, width: 8, borderRadius: "50%" }, style.dot]}
    />
  );
}
